import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "..\/..\/interfaces\/DecisionPriority";
import {
  Board,
  Move,
  IMove,
  IPiece,
  Position,
  PieceType,
} from "..\/..\/..\/..\/types";
import {
  MCTSUtils_v2,
  cloneFullGameState,
  getPieceFromPosition,
  applyMoveWithSnakeRespawn,
} from "..\/..\/..\/utils\/MCTSUtils";
import { MCTSMoveGenerationService } from "..\/..\/..\/utils\/MCTSMoveGenerationService";

/**
 * Priority 21: Two Turn Capture Defence
 *
 * ENHANCED: 4-ply simulation to defend against 2-turn piece capture threats
 *
 * This priority SOLELY looks for inevitable 2-turn captures of our non-king pieces!
 * It does NOT check for the king since it has its own priority higher up the list.
 * It does NOT look for general advancement or race moves.
 * It does NOT look for trade opportunities.
 *
 * CRITICAL IMPLEMENTATION:
 * 1. PURE 4-PLY SIMULATION: Enemy setup → Our defense → Enemy capture → Result
 * 2. Target identification: Find our non-king pieces that will be captured in 2 turns
 * 3. Defense verification: Ensure our defensive moves actually prevent the capture
 * 4. No hanging: Defensive moves must not hang our pieces
 * 5. Detailed logging: Shows threats, defensive moves, and 4-ply analysis
 *
 * Analysis:
 * 1. Find all enemy 2-turn capture threats against our non-king pieces
 * 2. For each threat, simulate our defensive responses
 * 3. 4-ply: Enemy Setup → Our Defense → Enemy Capture Attempt → Result
 * 4. Early exit if critical defense is found
 */
export class TwoTurnCaptureDefence extends BaseDecisionPriority {
  readonly priority = 21;
  readonly name = "Two Turn Capture Defence";
  readonly description =
    "CRITICAL: 4-ply simulation to defend against inevitable 2-turn piece captures (non-king pieces only)";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    const gameState = context.gameState;
    const isPlayerTurn = gameState.isLightTurn;

    // Find our non-king pieces that could be threatened
    const ourNonKingPieces = this.findOurNonKingPieces(gameState, isPlayerTurn);
    if (ourNonKingPieces.length === 0) return false;

    // Quick check: Are there any enemy pieces that could threaten our pieces?
    const enemyPieces = this.findEnemyPieces(gameState, isPlayerTurn);
    if (enemyPieces.length === 0) return false;

    return true; // Relevant if we have non-king pieces and enemy has threatening pieces
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];

    const gameState = context.gameState;
    const isPlayerTurn = gameState.isLightTurn;
    const enemyColor = isPlayerTurn ? "black" : "white";

    // STEP 1: Find all enemy 2-turn capture threats against our non-king pieces
    const enemyTwoTurnCaptureThreats = this.findEnemyTwoTurnCaptureThreats(
      gameState,
      enemyColor,
      isPlayerTurn
    );

    if (enemyTwoTurnCaptureThreats.length === 0) {
      this.addLog("✅ No enemy 2-turn capture threats detected");
      return {
        shouldEarlyExit: false,
        reasoning: "No 2-turn piece capture threats",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    this.addLog(
      `🚨 ENEMY 2-TURN CAPTURE THREATS: Found ${enemyTwoTurnCaptureThreats.length} capture sequences`
    );

    // STEP 2: Find defensive moves that block ALL 2-turn capture threats
    const defensiveMoves = this.findDefensiveMovesAgainstCaptureThreats(
      context.allPossibleMoves,
      gameState,
      enemyTwoTurnCaptureThreats,
      enemyColor
    );

    if (defensiveMoves.length === 0) {
      this.addLog("💀 NO DEFENSE: No moves can prevent 2-turn capture threats");
      return {
        shouldEarlyExit: false,
        reasoning: "No defense against 2-turn capture threats",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    // CRITICAL DEFENSE FOUND - Early exit
    this.addLog(
      `🛡️ CRITICAL CAPTURE DEFENSE: ${defensiveMoves.length} moves prevent 2-turn captures`
    );

    if (defensiveMoves.length === 1) {
      const bestDefense = defensiveMoves[0];
      this.addLog(
        `🛡️ ONLY DEFENSE: [${bestDefense.fromPosition.row},${bestDefense.fromPosition.col}] → [${bestDefense.move.position.row},${bestDefense.move.position.col}]`
      );

      return {
        shouldEarlyExit: true,
        selectedMove: bestDefense,
        reasoning: `${this.name}: CRITICAL - Only defense against 2-turn capture threats`,
        priority: this.priority,
        diagnostics: {
          twoTurnCaptureDefense: true,
          captureThreatsFound: enemyTwoTurnCaptureThreats.length,
          defensesFound: defensiveMoves.length,
          logs: this.analysisLogs,
        },
      };
    } else {
      // Multiple defenses found - pick the best one for early exit
      const bestDefense = this.selectBestDefensiveMove(
        defensiveMoves,
        gameState,
        enemyTwoTurnCaptureThreats
      );

      this.addLog(
        `🛡️ BEST CAPTURE DEFENSE: Selected [${bestDefense.fromPosition.row},${bestDefense.fromPosition.col}] → [${bestDefense.move.position.row},${bestDefense.move.position.col}] from ${defensiveMoves.length} options`
      );

      return {
        shouldEarlyExit: true,
        selectedMove: bestDefense,
        reasoning: `${this.name}: CRITICAL - Best defense against 2-turn capture threats`,
        priority: this.priority,
        diagnostics: {
          twoTurnCaptureDefense: true,
          captureThreatsFound: enemyTwoTurnCaptureThreats.length,
          defensesFound: defensiveMoves.length,
          logs: this.analysisLogs,
        },
      };
    }
  }

  /**
   * Find all enemy 2-turn capture threats against our non-king pieces
   * Returns: Array of {setupMove, captureMove, targetPiece} threat sequences
   */
  private findEnemyTwoTurnCaptureThreats(
    gameState: any,
    enemyColor: "white" | "black",
    isPlayerTurn: boolean
  ): Array<{
    setupMove: MoveInfo;
    captureMove: MoveInfo;
    targetPiece: { piece: IPiece; position: Position };
  }> {
    const captureThreats: Array<{
      setupMove: MoveInfo;
      captureMove: MoveInfo;
      targetPiece: { piece: IPiece; position: Position };
    }> = [];

    // Find our non-king pieces that could be targets
    const ourNonKingPieces = this.findOurNonKingPieces(gameState, isPlayerTurn);

    this.addLog(
      `🎯 Analyzing ${ourNonKingPieces.length} non-king pieces for capture threats`
    );

    // Generate enemy's first moves (setup moves)
    const originalTurn = gameState.isLightTurn;
    gameState.isLightTurn = enemyColor === "white";

    try {
      const moveService = MCTSMoveGenerationService.getInstance();
      const enemySetupMoves = moveService.getAllMoves(gameState);

      this.addLog(
        `🔍 Enemy has ${enemySetupMoves.length} setup moves to analyze`
      );

      // For each setup move, check if it leads to a 2-turn capture
      for (const setupMove of enemySetupMoves) {
        // Simulate the setup move
        const stateAfterSetup = this.simulateMove(gameState, setupMove);
        if (!stateAfterSetup) continue;

        // Our turn would be skipped in this simulation - go straight to enemy's capture attempt
        stateAfterSetup.isLightTurn = enemyColor === "white";

        // Generate enemy's capture moves from new position
        const enemyCaptureAttempts = moveService.getAllMoves(stateAfterSetup);

        // Check if any capture move captures our non-king pieces
        for (const captureMove of enemyCaptureAttempts) {
          const targetPiece = this.getPieceAt(
            stateAfterSetup.board,
            captureMove.move.position
          );

          // Check if this captures one of our non-king pieces
          if (
            targetPiece &&
            targetPiece.isLight === isPlayerTurn &&
            !targetPiece.isKing
          ) {
            const targetInfo = {
              piece: targetPiece,
              position: captureMove.move.position,
            };

            captureThreats.push({
              setupMove,
              captureMove,
              targetPiece: targetInfo,
            });

            this.addLog(
              `🚨 2-TURN CAPTURE THREAT: Setup [${setupMove.fromPosition.row},${
                setupMove.fromPosition.col
              }] → [${setupMove.move.position.row},${
                setupMove.move.position.col
              }], then capture ${this.getPieceTypeString(
                targetPiece.type
              )} at [${captureMove.move.position.row},${
                captureMove.move.position.col
              }]`
            );
          }
        }
      }
    } finally {
      gameState.isLightTurn = originalTurn;
    }

    return captureThreats;
  }

  /**
   * Find defensive moves that prevent ALL 2-turn capture threats
   * Uses 4-ply simulation to verify defense effectiveness
   */
  private findDefensiveMovesAgainstCaptureThreats(
    allMoves: MoveInfo[],
    gameState: any,
    captureThreats: Array<{
      setupMove: MoveInfo;
      captureMove: MoveInfo;
      targetPiece: { piece: IPiece; position: Position };
    }>,
    enemyColor: "white" | "black"
  ): MoveInfo[] {
    const defensiveMoves: MoveInfo[] = [];

    this.addLog(
      `🛡️ Testing ${allMoves.length} moves for capture defense capability`
    );

    for (const ourMove of allMoves) {
      if (
        this.canMoveDefendAgainstAllCaptureThreats(
          ourMove,
          gameState,
          captureThreats,
          enemyColor
        )
      ) {
        // Check if defensive move is hanging before accepting it
        if (this.isMoveHanging(ourMove, gameState)) {
          this.addLog(
            `❌ HANGING DEFENSE: [${ourMove.fromPosition.row},${ourMove.fromPosition.col}] → [${ourMove.move.position.row},${ourMove.move.position.col}] defends but hangs piece - REJECTED`
          );
          continue; // Skip hanging defensive moves
        }

        defensiveMoves.push(ourMove);
        this.addLog(
          `✅ SAFE CAPTURE DEFENSE: [${ourMove.fromPosition.row},${ourMove.fromPosition.col}] → [${ourMove.move.position.row},${ourMove.move.position.col}] blocks all capture threats`
        );
      }
    }

    return defensiveMoves;
  }

  /**
   * 4-PLY SIMULATION: Check if our move defends against all enemy 2-turn capture threats
   * PLY 1: Enemy setup move
   * PLY 2: Our defensive move
   * PLY 3: Enemy capture attempt
   * PLY 4: Check if capture is still possible
   */
  private canMoveDefendAgainstAllCaptureThreats(
    ourMove: MoveInfo,
    gameState: any,
    captureThreats: Array<{
      setupMove: MoveInfo;
      captureMove: MoveInfo;
      targetPiece: { piece: IPiece; position: Position };
    }>,
    enemyColor: "white" | "black"
  ): boolean {
    // Test against each capture threat
    for (const threat of captureThreats) {
      if (
        !this.doesOurMoveBlockCaptureSequence(
          ourMove,
          gameState,
          threat,
          enemyColor
        )
      ) {
        return false; // If we can't block one threat, defense fails
      }
    }

    return true; // All threats blocked
  }

  /**
   * 4-PLY SIMULATION: Test if our defensive move blocks a specific capture sequence
   */
  private doesOurMoveBlockCaptureSequence(
    ourMove: MoveInfo,
    gameState: any,
    threat: {
      setupMove: MoveInfo;
      captureMove: MoveInfo;
      targetPiece: { piece: IPiece; position: Position };
    },
    enemyColor: "white" | "black"
  ): boolean {
    // PLY 1: Simulate enemy setup move
    const stateAfterEnemySetup = this.simulateMove(gameState, threat.setupMove);
    if (!stateAfterEnemySetup) return false;

    // PLY 2: Simulate our defensive move
    stateAfterEnemySetup.isLightTurn = !(enemyColor === "white");
    const stateAfterOurDefense = this.simulateMove(
      stateAfterEnemySetup,
      ourMove
    );
    if (!stateAfterOurDefense) return false;

    // PLY 3: Try enemy capture attempt
    stateAfterOurDefense.isLightTurn = enemyColor === "white";

    // Check if the enemy can still execute the capture
    const moveService = MCTSMoveGenerationService.getInstance();
    const enemyMovesAfterDefense =
      moveService.getAllMoves(stateAfterOurDefense);

    // PLY 4: Check if the original capture is still possible
    for (const enemyMove of enemyMovesAfterDefense) {
      // Check if enemy can still capture the target piece
      const targetAtOriginalPosition = this.getPieceAt(
        stateAfterOurDefense.board,
        threat.targetPiece.position
      );

      if (
        targetAtOriginalPosition &&
        targetAtOriginalPosition.isLight === threat.targetPiece.piece.isLight &&
        targetAtOriginalPosition.type === threat.targetPiece.piece.type &&
        enemyMove.move.position.row === threat.targetPiece.position.row &&
        enemyMove.move.position.col === threat.targetPiece.position.col
      ) {
        // Enemy can still capture the target piece - defense failed
        return false;
      }
    }

    // If we get here, the capture is no longer possible - defense succeeded
    return true;
  }

  /**
   * Check if a move results in hanging the piece
   */
  private isMoveHanging(moveInfo: MoveInfo, gameState: any): boolean {
    try {
      // Simulate our move
      const stateAfterMove = this.simulateMove(gameState, moveInfo);
      if (!stateAfterMove) return true;

      // Switch to enemy turn
      const enemyColor = gameState.isLightTurn ? "black" : "white";
      stateAfterMove.isLightTurn = enemyColor === "white";

      // Check if enemy can capture our piece at the new position
      const moveService = MCTSMoveGenerationService.getInstance();
      const enemyMoves = moveService.getAllMoves(stateAfterMove);

      for (const enemyMove of enemyMoves) {
        if (
          enemyMove.move.position.row === moveInfo.move.position.row &&
          enemyMove.move.position.col === moveInfo.move.position.col
        ) {
          // Enemy can capture our piece - check if we can recapture
          const stateAfterCapture = this.simulateMove(
            stateAfterMove,
            enemyMove
          );
          if (!stateAfterCapture) continue;

          // Switch back to our turn
          stateAfterCapture.isLightTurn = !(enemyColor === "white");

          // Check if we can recapture
          const ourRecaptureMoves = moveService.getAllMoves(stateAfterCapture);
          let canRecapture = false;

          for (const recaptureMove of ourRecaptureMoves) {
            if (
              recaptureMove.move.position.row === moveInfo.move.position.row &&
              recaptureMove.move.position.col === moveInfo.move.position.col
            ) {
              canRecapture = true;
              break;
            }
          }

          if (!canRecapture) {
            return true; // Enemy can capture and we can't recapture = hanging!
          }
        }
      }

      return false; // No hanging found
    } catch (error) {
      return true; // Assume hanging if analysis fails
    }
  }

  /**
   * Select the best defensive move from multiple options
   * Prioritizes: 1) Moves that defend the most valuable pieces, 2) Moves that maintain position
   */
  private selectBestDefensiveMove(
    defensiveMoves: MoveInfo[],
    gameState: any,
    captureThreats: Array<{
      setupMove: MoveInfo;
      captureMove: MoveInfo;
      targetPiece: { piece: IPiece; position: Position };
    }>
  ): MoveInfo {
    // For now, return the first defensive move
    // Could be enhanced to prioritize based on piece value, position, etc.
    this.addLog(
      `🛡️ DEFENSE SELECTION: Choosing from ${defensiveMoves.length} defensive moves`
    );
    return defensiveMoves[0];
  }

  /**
   * Find all our non-king pieces on the board
   */
  private findOurNonKingPieces(
    gameState: any,
    isPlayerTurn: boolean
  ): Array<{ piece: IPiece; position: Position }> {
    const ourPieces: Array<{ piece: IPiece; position: Position }> = [];

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = this.getPieceAt(gameState.board, { row, col });
        if (piece && piece.isLight === isPlayerTurn && !piece.isKing) {
          ourPieces.push({
            piece,
            position: { row, col },
          });
        }
      }
    }

    return ourPieces;
  }

  /**
   * Find all enemy pieces on the board
   */
  private findEnemyPieces(
    gameState: any,
    isPlayerTurn: boolean
  ): Array<{ piece: IPiece; position: Position }> {
    const enemyPieces: Array<{ piece: IPiece; position: Position }> = [];

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = this.getPieceAt(gameState.board, { row, col });
        if (piece && piece.isLight !== isPlayerTurn) {
          enemyPieces.push({
            piece,
            position: { row, col },
          });
        }
      }
    }

    return enemyPieces;
  }

  /**
   * Simulate a move and return the new game state
   */
  private simulateMove(gameState: any, moveInfo: MoveInfo): any | null {
    try {
      const newState = cloneFullGameState(gameState);
      const piece = getPieceFromPosition(newState, moveInfo.fromPosition);
      if (!piece) return null;

      applyMoveWithSnakeRespawn(
        newState,
        moveInfo.fromPosition,
        moveInfo.move,
        piece
      );

      return newState;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get piece at a specific position
   */
  private getPieceAt(board: any[][], position: Position): IPiece | null {
    try {
      if (
        position.row < 0 ||
        position.row >= board.length ||
        position.col < 0 ||
        position.col >= board[0].length
      ) {
        return null;
      }

      const square = board[position.row][position.col];
      if (!square || !square.Pieces || square.Pieces.length === 0) {
        return null;
      }

      return square.Pieces[0]; // Return the first piece (typically only one piece per square)
    } catch (error) {
      return null;
    }
  }

  /**
   * Get piece type as string for logging
   */
  private getPieceTypeString(type: PieceType): string {
    switch (type) {
      case PieceType.Bear:
        return "Bear";
      case PieceType.Crane:
        return "Crane";
      case PieceType.Frog:
        return "Frog";
      case PieceType.Fungi:
        return "Fungi";
      case PieceType.Goat:
        return "Goat";
      case PieceType.Monkey:
        return "Monkey";
      case PieceType.Octopus:
        return "Octopus";
      case PieceType.Penguin:
        return "Penguin";
      case PieceType.Rhino:
        return "Rhino";
      case PieceType.Snake:
        return "Snake";
      case PieceType.Tree:
        return "Tree";
      case PieceType.Virus:
        return "Virus";
      case PieceType.Wolf:
        return "Wolf";
      default:
        return "Unknown";
    }
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }
}

